import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from 'context/ThemeContext'
import SideNavBar from '../SideNavBar'

import { Settings, LogOut, Menu as MenuIcon, Bell, Users, ListChecks, Search, Bookmark, InfoIcon, Send, FileText } from 'lucide-react' 
import GlobalSearch from 'components/Search/GlobalSearch'
import { getSearchCopy } from 'components/Search/searchCopy'
import { getSearchLocale } from 'components/Search/searchUtils'
import GraphExploreButton from 'ui/GraphExploreButton'
import { useUser } from 'util/UserContext';
import { handleLogout } from 'components/Auth/AuthLogout'
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import LoadingSkeleton from 'ui/LoadingSkeleton'
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal'
import PostCreateButton from 'ui/PostCreateButton'
import { useNotificationsModal } from 'context/NotificationsModalContext'
import ReleaseNotesModal from 'components/Common/ReleaseNotesModal'
import { isBetaVersion } from 'util/appVersion'
import Modal from 'ui/Modal'
import { hasExistingAccountCookie } from 'util/existingUserCookie'
import ActorAvatar from 'components/Actors/ActorAvatar'
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes'
import { toActorFromUser } from 'util/actorIdentity'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/Tooltip'
import { LAYER_TOKENS } from 'ui/layers'
import { Sheet, SheetContent } from 'ui/Sheet'
import ChatInbox from 'components/Chat/ChatInbox'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useChatUnreadCountsQuery } from 'hooks/queries/useChatQueries'
import { useNotificationUnreadCount } from 'hooks/queries/useNotificationQueries'
import { useDraftCountQuery } from 'hooks/queries/useDraftsQuery'
import { countLocalDrafts } from 'util/localDrafts'
import { getDraftUiStrings, resolveDraftLocale } from 'i18n/draftUi'

interface AppTooltipProps {
  title: string;
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const AppTooltip: React.FC<AppTooltipProps> = ({ title, children, side = 'bottom' }) => {
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side}>{title}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const AppBar = () => {
  const { currentUser, setShowAuthModal, userIsMobile, awaitingUser, setShowUserOnboardModal, userOnboardingTasks } = useUser();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchLocale = getSearchLocale(location.search);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openAddImpactModal, setOpenAddImpactModal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { open: openNotificationsModal } = useNotificationsModal();
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [selectedChatConversationId, setSelectedChatConversationId] = useState<string | null>(null);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [localDraftCount, setLocalDraftCount] = useState(0);
  const searchInputContainerRef = useRef<HTMLDivElement>(null);
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  
  // Determine if this is a beta version
  const isBeta = isBetaVersion();

  const showOnboardingIndicator = userOnboardingTasks !== null && userOnboardingTasks < 5;
  const { isEnabled: chatEnabled } = useFeatureFlag("chat", Boolean(currentUser));
  const { data: chatUnreadCounts } = useChatUnreadCountsQuery(Boolean(currentUser) && chatEnabled);
  const chatBadgeCount = (chatUnreadCounts?.total_unread || 0) + (chatUnreadCounts?.requests || 0);
  const { data: notificationUnreadData } = useNotificationUnreadCount(Boolean(currentUser));
  const { data: draftCountData } = useDraftCountQuery(Boolean(currentUser));
  const unreadCount = notificationUnreadData?.count ?? 0;
  const draftCount = (draftCountData?.total ?? 0) + localDraftCount;
  const currentUserActor = toActorFromUser(currentUser);

  useEffect(() => {
    if (!currentUser) {
      setLocalDraftCount(0);
      return;
    }
    const refreshLocalDraftCount = () => setLocalDraftCount(countLocalDrafts(currentUser.id));
    refreshLocalDraftCount();
    const intervalId = window.setInterval(refreshLocalDraftCount, 30_000);
    window.addEventListener('focus', refreshLocalDraftCount);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshLocalDraftCount);
    };
  }, [currentUser, menuOpen, drawerOpen]);

  /**
   * Render the current user's avatar inside the AppBar / nav menus.
   * `clickTo="none"` because the avatar always sits inside an
   * already-clickable parent (dropdown trigger, drawer trigger, menu
   * row). The story ring still appears when the viewer has an active
   * story so the rest of the app can surface that affordance from the
   * navigation surface.
   */
  const renderProfileAvatar = (className: string = '') => (
    <ActorAvatar
      actor={currentUserActor}
      size={ACTOR_AVATAR_SIZES.message}
      clickTo="none"
      className={className}
    />
  );

  const UserOptions = () => (
    <>
      {!userIsMobile ? (
        <div className="mt-2 flex flex-col p-2 min-w-48 space-y-1">
          {userOnboardingTasks !== null && userOnboardingTasks < 5 && (
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            setShowUserOnboardModal(true);
          }}>
            <div className="flex items-center gap-3">
              <ListChecks className="size-5 mr-1"/>
              <span>Enhance your profile</span><span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">({userOnboardingTasks} / 5)</span>              
              <span className="size-[12px] relative rounded-full bg-eoe-red animate-pulse"/>
            </div>
          </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate(`/user/${currentUser?.username}`);
          }} className="px-0">
            <div className="flex items-center gap-2">
              {renderProfileAvatar()}
              <div className="flex flex-col">
                <span className="text-sm">View Profile</span>
                <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{currentUser?.username}</span>
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate(`/user/${currentUser?.username}/drafts`);
          }}>
            <div className="flex w-full items-center justify-between gap-4">
              <span className="flex items-center gap-3">
                <FileText className="size-5 mr-1"/>
                <span>{draftUi.appBarLabel}</span>
              </span>
              <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{draftCount}</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate('/parteas/member');
          }}>
            <div className="flex items-center gap-3">
              <Users className="size-5 mr-1"/>
              <span>My Parteas</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate(`/user/${currentUser?.username}/saved`);
          }}>
            <div className="flex items-center gap-3">
              <Bookmark className="size-5 mr-1"/>
              <span>Saved</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            handleLogout({ redirectToHome: false });
          }}>
            <div className="flex items-center gap-3">
              <LogOut className="size-5 mr-1"/>
              <span>Sign out</span>
            </div>
          </DropdownMenuItem>
          <hr className="menu-divider w-full border-eoe-border-main dark:border-eoe-dark-border-main my-2"/>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate('/settings/');
          }}>
            <div className="flex items-center gap-3">
              <Settings className="size-5 mr-1"/>
              <span>Settings</span>
            </div>
          </DropdownMenuItem>
        </div>
      ) : (
        <div className="flex flex-col [&>:not(button):not(hr)]:px-3 [&>:not(button):not(hr)]:py-2">
          {userOnboardingTasks !== null && userOnboardingTasks < 5 && (
            <button 
              onClick={() => {
                setDrawerOpen(false);
                setShowUserOnboardModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
            >
              <ListChecks className="size-5 mr-1.5"/>
              <span>Enhance your profile</span><span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">({userOnboardingTasks} / 5)</span>              
              <span className="size-[12px] relative rounded-full bg-eoe-red animate-pulse"/>
            </button>
          )}
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate(`/user/${currentUser?.username}`);
            }}
            className="flex items-center gap-2 px-[10px] py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            {renderProfileAvatar()}
            <div className="flex flex-col text-left">
              <span className="text-sm">View Profile</span>
              <span className="text-xs">{currentUser?.username}</span>
            </div>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate(`/user/${currentUser?.username}/drafts`);
            }}
            className="flex items-center justify-between gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <span className="flex items-center gap-2">
              <FileText className="size-5 mr-1.5"/>
              <span>{draftUi.appBarLabel}</span>
            </span>
            <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{draftCount}</span>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate('/parteas/member');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Users className="size-5 mr-1.5"/>
            <span>My Parteas</span>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate(`/user/${currentUser?.username}/saved`);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Bookmark className="size-5 mr-1.5"/>
            <span>Saved</span>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              handleLogout({ redirectToHome: false });
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <LogOut className="size-5 mr-1.5"/>
            <span>Sign out</span>
          </button>
          <hr className="menu-divider border-eoe-border-main dark:border-eoe-dark-border-main m-2"/>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate('/settings/');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Settings className="size-5 mr-1.5"/>
            <span>Settings</span>
          </button>
        </div>
      )}
    </>
  )

  const [hasExistingCookie, setHasExistingCookie] = useState<boolean>(() => hasExistingAccountCookie())

  useEffect(() => {
    setHasExistingCookie(hasExistingAccountCookie())
  }, [awaitingUser, currentUser])

  const handleLoginButtonClick = () => {
    if (awaitingUser) {
      return
    }
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      navigate('/');
    }
  };

  // Handle scroll to top functionality
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setIsDrawerOpen(open);
  };


  return (
    <div className="dark:bg-eoe-dark-bg">
      <header
        className="AppBar-root fixed inset-x-0 top-0 border-b border-[#c8c8c8] bg-white transition-colors duration-200 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
        style={{ zIndex: LAYER_TOKENS.appBar }}
      >
        <div className="Toolbar-root flex h-14 items-center px-2 md:px-3">
          {/* Left: Menu + Logo */}
          <div className="flex gap-1 shrink-0 items-center justify-start">
            <button
              aria-label="side navigation menu"
              className="hover:bg-gray-300 dark:hover:bg-gray-700 dark:text-gray-300 rounded-full size-8 lg:hidden"
              onClick={toggleDrawer(!isDrawerOpen)}
            >
              <MenuIcon className="size-6 ml-1 text-eoe-text dark:text-eoe-dark-text" />
            </button>
            <AppTooltip title="Return to home page">
              <a href="/" className="no-underline flex items-center">
                <span className="logo-earth-container pr-2 flex-1 items-center md:hidden relative">
                  <img
                    src="/soc_brandmark.svg"
                    alt="Societea Brand Mark"
                    className="logo size-10 bg-white dark:bg-eoe-dark-bg"
                  />
                </span>
                <span className="logo-text-container ml-1 hidden md:flex items-center md:my-2 mr-2 relative">
                  <img
                    src={theme === 'light' ? '/soc_app_logo_head_light.svg' : '/soc_app_logo_head_dark.svg'}
                    alt="Societea Logo full"
                    className="logo-text h-8 w-auto"
                  />
                </span>
                {isBeta && (
                  <span className="flex items-center md:ml-1">
                    <span
                      onClick={(e) => {
                      }}
                      className="relative right-4 md:right-2 top-2.5 text-xxs bg-indigo-600 dark:bg-indigo-600 text-white pl-[5px] pr-1 py-0.5 rounded-full hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors flex-shrink-0"
                    >
                      BETA
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowReleaseNotes(true);
                      }}
                      className="relative right-4 md:right-2 top-0.5 flex-shrink-0 text-eoe-gray dark:text-eoe-dark-text"
                    >
                      <InfoIcon className="size-3.5"/>
                    </button>
                  </span>
                )}
              </a>
            </AppTooltip>
          </div>

          {/* Middle: Clickable spacer + Search Input (Desktop) or Search Icon (Mobile) */}
          {userIsMobile ? (
            <div className="flex-1 flex items-center">
              {/* Clickable spacer area for mobile */}
              <div 
                className="flex-1 cursor-pointer h-full min-h-[40px]" 
                onClick={handleScrollToTop}
                aria-label="Scroll to top"
              />
              <div className="flex justify-end items-center">
                <button
                  onClick={() => setShowMobileSearch(true)}
                  className="rounded-full p-1 mx-1 text-eoe-gray dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover transition-colors"
                  aria-label={getSearchCopy(searchLocale, "searchTitle")}
                >
                  <Search className="size-5" />
                </button>
                <GraphExploreButton variant="icon" className="mx-0.5" />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center">
              {/* Clickable spacer area before search */}
              <div 
                className="flex-1 cursor-pointer h-full min-h-[40px]" 
                onClick={handleScrollToTop}
                aria-label="Scroll to top"
              />
              {/* Search container + Explore button */}
              <div
                ref={searchInputContainerRef}
                className="flex flex-col justify-stretch w-full ml-1"
              >
                {/* Wrap the input + 'X' in relative container */}
                <div className="relative w-full flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <GlobalSearch
                      className="w-full"
                      autoFocus={false}
                      fullScreen={false}
                    />
                  </div>
                  <GraphExploreButton variant="text" text="Explore" className="shrink-0" />
                </div>            
              </div>
              {/* Clickable spacer area after search */}
              <div 
                className="flex-1 cursor-pointer h-full min-h-[40px]" 
                onClick={handleScrollToTop}
                aria-label="Scroll to top"
              />
            </div>
          )}

          {/* Right: Auth / User Menu */}
          <div className="auth-user-buttons flex items-center gap-x-1">
          {awaitingUser ? (
            <div className="flex items-center">
              <LoadingSkeleton height="h-9" width="w-9" rounded='rounded-full'/>
            </div>
          ) : (!awaitingUser && currentUser ? (
            <div className="auth-user-buttons flex items-center gap-x-1">
              <div className="flex md:flex">
                <PostCreateButton
                    tooltip="Create a post"
                    className="flex md:hidden bg-transparent dark:bg-transparent rounded-full p-1 justify-center items-center text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-indigo-600 hover:text-white transition-colors"
                />
                <PostCreateButton
                  buttonText="Spill"
                  tooltip="Create a post"
                  className="hidden md:flex bg-transparent dark:bg-transparent rounded-full pr-3 pl-1 py-1.5 justify-center items-center gap-0.5 text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-indigo-600 hover:text-white transition-colors"
                />
              </div>
              {chatEnabled ? (
                <button
                  className="rounded-full p-1 justify-center items-center text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover cursor-pointer transition-colors"
                  onClick={() => {
                    if (userIsMobile) {
                      navigate("/chat");
                      return;
                    }
                    setSelectedChatConversationId(null);
                    setShowChatPanel(true);
                  }}
                  aria-label="Open chats"
                >
                  {chatBadgeCount > 0 ? (
                    // Explicit fixed-size relative container ensures consistent badge positioning across
                    // browsers (Chromium devtools vs iOS WebKit/Chrome on real iPhone). Top-right
                    // placement is the standard UX pattern for unread indicators.
                    <div className="relative w-5 h-5 flex items-center justify-center">
                      <Send className="size-5" />
                      <span className="size-2.5 absolute top-0 -right-1 rounded-full bg-eoe-red dark:bg-dark-warning-red border-2 border-white dark:border-eoe-dark-bg" />
                    </div>
                  ) : (
                    <Send className="size-5" />
                  )}
                </button>
              ) : null}
              <button
                className="rounded-full p-1 justify-center items-center text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover cursor-pointer transition-colors"
                onClick={openNotificationsModal}
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
              >
                {unreadCount < 1 ? (
                  <Bell className="size-5" />
                ) : (
                  <div className="relative w-5 h-5 flex items-center justify-center">
                    <Bell className="size-5" />
                    <span
                      aria-hidden="true"
                      className="absolute top-0 right-0 size-2.5 rounded-full bg-eoe-red dark:bg-dark-warning-red border-2 border-white dark:border-eoe-dark-bg"
                    />
                  </div>
                )}
              </button>
              {openAddImpactModal && (
                <SpillTheTeaModal
                    open={openAddImpactModal}
                    onClose={() => setOpenAddImpactModal(false)}
                    onImpactAdded={() => {}}
                    customTitle="Add an impact"
                    fixedPostType="impact"
                    hidePostTypeButtons={true}
                />
              )}
              <div className="flex-grow-0">
                {userIsMobile ? (
                  <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                    <DrawerTrigger asChild>
                      <button type="button" className="relative flex items-center justify-center">
                        {renderProfileAvatar()}
                        {showOnboardingIndicator && (
                          // Consistent absolute positioning on relative button container. The -right-0.5
                          // and top-0 values provide reliable top-right overlap on all devices including iOS.
                          <span className="size-[12px] absolute -right-0.5 top-0 rounded-full bg-eoe-red animate-pulse" />
                        )}
                      </button>
                    </DrawerTrigger>
                    <DrawerContent title='User Menu'>
                      <UserOptions />
                    </DrawerContent>
                  </Drawer>
                ) : (
                  <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="relative flex items-center justify-center">
                        {renderProfileAvatar()}
                        {showOnboardingIndicator && (
                          // Consistent absolute positioning on relative button container. The -right-0.5
                          // and top-0 values provide reliable top-right overlap on all devices including iOS.
                          <span className="size-[12px] absolute -right-0.5 top-0 rounded-full bg-eoe-red animate-pulse" />
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <UserOptions />
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ) : (
            <AppTooltip title={hasExistingCookie ? "Sign in" : "Sign up"}>
              <button
                className="rounded-3xl px-4 py-1.5 text-sm whitespace-nowrap dark:text-eoe-dark-text bg-soc-purple hover:bg-soc-primary text-white cursor-pointer transition-colors"
                onClick={handleLoginButtonClick}
              >
                {hasExistingCookie ? 'Sign in' : 'Sign up'}
              </button>
            </AppTooltip>
            ))}
          </div>
        </div>
      </header>
      
      {/* Mobile Search Modal */}
      {showMobileSearch && (
                 <Modal
           isOpen={showMobileSearch}
           onClose={() => setShowMobileSearch(false)}
           title=""
           displayStyle="cover"
           className="mobile-search-modal p-0 sm:p-0 max-w-full h-full sm:max-w-full sm:h-full sm:rounded-none overflow-hidden"
         >
           <div className="h-full w-full flex flex-col">
             {/* Search Input at top */}
             <div className="left-2 my-2 mx-4 bg-eoe-bg dark:bg-eoe-dark-bg-modal relative z-10">
               <GlobalSearch
                 className="w-full"
                 autoFocus={true}
                 fullScreen={true}
                 onClose={() => setShowMobileSearch(false)}
               />
             </div>
           </div>
         </Modal>
      )}
      
      <SideNavBar isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} onClick={() => setIsDrawerOpen(false)} />
      <Sheet open={showChatPanel} onOpenChange={(open) => {
        setShowChatPanel(open);
        if (!open) {
          setSelectedChatConversationId(null);
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-[420px] p-0">
          <ChatInbox
            embedded={true}
            selectedConversationPublicId={selectedChatConversationId}
            onSelectConversation={(conversationPublicId) => setSelectedChatConversationId(conversationPublicId)}
            onClearSelection={() => setSelectedChatConversationId(null)}
          />
        </SheetContent>
      </Sheet>
      {showReleaseNotes && (
        <ReleaseNotesModal
          isOpen={showReleaseNotes}
          onClose={() => setShowReleaseNotes(false)}
        />
      )}
    </div>
  );
};

export default AppBar;
